<script>
  import "../app.css";
  import Toasts from "$lib/components/Toasts.svelte";
  import { P, Spinner } from "flowbite-svelte";
  import { onMount, onDestroy } from "svelte";
  import { browser } from "$app/environment";
  import { page } from "$app/stores";
  import { goto } from "$app/navigation";
  import Footer from "$lib/Landing/Footer.svelte";
  import Nav from "$lib/Landing/Nav.svelte";
  import GuidedTour from "$lib/components/GuidedTour.svelte";
  import SupportChat from "$lib/SupportChat.svelte";
  import { getUser } from "$service/http_calls";
  import { connected, connectSSE, disconnectSSE } from "$service/sse.js";
  import { authenticated_user, current_user, darkMode } from "$service/store";
  import { claimSavedContent } from "$service/saved_content";

  let page_loading = false;

  // The last path segment, reactive to client-side navigation (was read once
  // on mount from window.location in the old App.svelte).
  $: slug = $page.url.pathname.split("/").filter(Boolean).pop() ?? "";

  // Keep the dark-mode classes in sync with route + preference. Disabled on the
  // root path, login and signup (per CLAUDE.md), enabled elsewhere.
  $: if (typeof document !== "undefined") {
    const allowDark = slug !== "" && slug !== "login" && slug !== "signup";
    if (allowDark && $darkMode) {
      document.documentElement.classList.add("dark");
      document.body.classList.add("bg-gray-900", "text-white");
    } else {
      document.documentElement.classList.remove("dark");
      document.body.classList.remove("bg-gray-900", "text-white");
    }
  }

  // NOTE: No reactive auth guard here. Calling goto() from a reactive `$:` block
  // re-fires whenever its dependencies change and races deliberate navigations
  // (e.g. logout, which clears current_user AND navigates) — that froze the app.
  // Protection is handled the same way the original SPA did it: onMount
  // authenticate() redirects on direct loads/refreshes, and the nav only shows
  // protected links while authenticated, so logged-out users can't click into
  // them. (Session-expiry mid-session is best handled by a 401 interceptor in
  // http_calls, not a navigation guard.)

  const handleBeforeUnload = () => {
    disconnectSSE();
  };

  onMount(async () => {
    if (!localStorage.getItem("isCleared")) {
      localStorage.clear();
      localStorage.setItem("isCleared", "true");
    }

    if (
      slug != "login" &&
      slug != "signup" &&
      slug != "early-access" &&
      slug != "terms" &&
      slug != ""
    ) {
      console.log("will try to authenticate...");
      page_loading = true;
    }

    // Always check the session, also on public pages, so a cached user from
    // an expired session doesn't keep showing the app nav.
    await authenticate();

    if ($current_user && $connected == "false") {
      connectSSE();
    }

    window.addEventListener("beforeunload", handleBeforeUnload);
  });

  // Cleanup SSE connection when component is destroyed.
  // NOTE: onDestroy runs during SSR/prerender too (unlike onMount), so this
  // body — which touches window + sessionStorage (via disconnectSSE) — must be
  // browser-gated or it crashes the build-time render of the public pages.
  onDestroy(() => {
    if (!browser) return;
    disconnectSSE();
    window.removeEventListener("beforeunload", handleBeforeUnload);
  });

  // Send users back to the page they asked for once they have logged in.
  function loginWithNext() {
    const next = $page.url.pathname + $page.url.search;
    return `/login?next=${encodeURIComponent(next)}`;
  }

  async function authenticate() {
    current_user.set(null);
    try {
      let auth_resp = await getUser();
      page_loading = false;
      if (typeof auth_resp?.id === "undefined") {
        console.log("should redirect right?");
        if (
            slug != "login" &&
            slug != "signup" &&
            slug != "terms" &&
            slug != ""
          ) {
            goto(loginWithNext());
        } else {
          current_user.set(null);
          authenticated_user.set(null);
        }
      } else if (auth_resp.enabled === false) {
        current_user.set(null);
        authenticated_user.set(null);
        if (slug != "login" && slug != "signup" && slug != "terms" && slug != "") {
          goto("/login?disabled=true");
        }
      } else {
        // A sign-in that bypassed the login form (another tab, single sign-on)
        // is checked here too.
        if (claimSavedContent(auth_resp)) {
          window.location.reload();
          return;
        }
        current_user.set(auth_resp);
        authenticated_user.set(auth_resp);
      }
    } catch (e) {
      console.log("error authenticating", e);

      current_user.set(null);
      authenticated_user.set(null);
      if (
        slug != "login" &&
        slug != "signup" &&
        slug != "terms" &&
        slug != ""
      ) {
        goto(loginWithNext());
      }
    }
  }
</script>

<svelte:head>
  <!-- Default title for every route; public pages override it (and add their
       own description / OG / canonical) in their own <svelte:head>. Svelte
       keeps only the most specific <title>. -->
  <title>SmartClaim - AI for UK R&D tax relief consultants</title>
  <!-- Link-preview card shared by every page. Pages must not repeat these:
       Svelte only de-duplicates <title>, so a second og:image would be kept. -->
  <meta property="og:site_name" content="SmartClaim" />
  <meta property="og:locale" content="en_GB" />
  <meta property="og:image" content="https://smartclaim.uk/og-image.png" />
  <meta property="og:image:width" content="1200" />
  <meta property="og:image:height" content="630" />
  <meta property="og:image:alt" content="SmartClaim - Your AI Tool for UK R&D Tax Narratives" />
  <meta name="twitter:card" content="summary_large_image" />
</svelte:head>

<div class="relative min-h-screen flex flex-col {slug === 'login' || slug === 'signup' ? 'bg-cream' : slug === '' ? '' : 'bg-white'} dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-geist">
  {#if slug === ""}
    <!-- Cream diagonal background (Figma node 2043:1626) covering nav + hero -->
    <div class="hidden md:block absolute pointer-events-none" style="top: 0; left: 0; width: 100%; height: 748px; background-color: #f5f2ed; clip-path: polygon(0 0, 100% 0, 100% 40%, 0 100%); z-index: 0;"></div>
  {/if}
  <Nav />


  {#if page_loading}
    <div class="flex justify-center items-center mt-10">
      <div class="flex flex-col items-center">
        <Spinner class="my-10"></Spinner>
        <P weight="bold">Loading, please wait...</P>
      </div>
    </div>
  {:else}
    <slot />
  {/if}

  <Toasts />

  {#if $current_user && slug != "" && typeof $authenticated_user?.id !== "undefined"}
    <SupportChat />
  {/if}

  <GuidedTour />


  {#if !page_loading}<Footer></Footer>{/if}
</div>
